/**
 * VAPID public-key validation.
 *
 * A VAPID application server public key (RFC 8292 §2) is an **uncompressed
 * P-256 elliptic-curve point** serialised as 65 bytes:
 *
 *   byte[0]     = 0x04  (uncompressed-point marker)
 *   byte[1..32] = X coordinate (big-endian)
 *   byte[33..64] = Y coordinate (big-endian)
 *
 * The key is transmitted as a URL-safe Base64 (base64url) string with no
 * padding (as specified by RFC 8292 §2 and the Web Push spec).
 *
 * Pure-logic + SubtleCrypto only — no Service Worker or pushManager calls.
 */

/** Union result returned by {@link validateVapidPublicKey}. */
export type VapidValidationResult =
  | { readonly ok: true; readonly keyBytes: Uint8Array }
  | {
      readonly ok: false;
      readonly reason: "wrongLength" | "badBase64" | "notP256";
    };

/**
 * Expected byte length of an uncompressed P-256 public key.
 * 1 byte prefix (0x04) + 32 bytes X + 32 bytes Y = 65 bytes.
 */
const UNCOMPRESSED_P256_BYTE_LENGTH = 65;

/** The uncompressed-point prefix mandated by SEC 1 §2.3.4. */
const UNCOMPRESSED_POINT_PREFIX = 0x04;

// ---------------------------------------------------------------------------
// Base64url decode
// ---------------------------------------------------------------------------

/**
 * Decode a base64url (no-padding) string to a Uint8Array.
 *
 * Returns `null` if the string contains characters outside the base64url
 * alphabet or cannot be decoded.
 */
function decodeBase64url(input: string): Uint8Array | null {
  // Convert base64url to standard base64 by swapping - → + and _ → /
  // then adding padding as needed.
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);

  // Validate the alphabet before calling atob so we can return a typed error
  // rather than a thrown DOMException.
  if (!/^[A-Za-z0-9+/=]*$/.test(padded)) {
    return null;
  }

  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// SubtleCrypto import
// ---------------------------------------------------------------------------

/**
 * Attempt to import raw key bytes as a P-256 ECDSA public key.
 *
 * Returns `true` if SubtleCrypto accepts the key, `false` if it rejects it
 * (e.g. the point is not on the P-256 curve).
 *
 * The `extractable: false` flag is intentional — we never need to export this
 * validation-only key object.
 */
async function tryImportP256PublicKey(keyBytes: Uint8Array): Promise<boolean> {
  try {
    await crypto.subtle.importKey(
      "raw",
      keyBytes.buffer as ArrayBuffer,
      { name: "ECDSA", namedCurve: "P-256" },
      /* extractable */ false,
      /* key usages */ ["verify"],
    );
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate a VAPID application server public key supplied as a base64url
 * (no-padding) string.
 *
 * Validation steps, in order:
 * 1. Decode from base64url → reject with `badBase64` on failure.
 * 2. Check byte length == 65 → reject with `wrongLength` otherwise.
 * 3. Check leading byte == 0x04 (uncompressed-point marker) →
 *    reject with `notP256` if the byte is anything else.
 * 4. Ask SubtleCrypto to import the key as a raw P-256 ECDSA public key →
 *    reject with `notP256` if the import fails (point not on curve, etc.).
 *
 * On success returns `{ ok: true, keyBytes }` where `keyBytes` is the raw
 * 65-byte Uint8Array so callers can use the bytes directly without
 * re-decoding.
 */
export async function validateVapidPublicKey(
  base64url: string,
): Promise<VapidValidationResult> {
  // Step 1: base64url decode.
  const keyBytes = decodeBase64url(base64url);
  if (keyBytes === null) {
    return { ok: false, reason: "badBase64" };
  }

  // Step 2: length check.
  if (keyBytes.length !== UNCOMPRESSED_P256_BYTE_LENGTH) {
    return { ok: false, reason: "wrongLength" };
  }

  // Step 3: leading-byte check.
  if (keyBytes[0] !== UNCOMPRESSED_POINT_PREFIX) {
    return { ok: false, reason: "notP256" };
  }

  // Step 4: SubtleCrypto import.
  const importable = await tryImportP256PublicKey(keyBytes);
  if (!importable) {
    return { ok: false, reason: "notP256" };
  }

  return { ok: true, keyBytes };
}
